import { Injectable } from '@angular/core';
import { openDB, IDBPDatabase } from 'idb';
import { UserConfig } from '@models/user-config.model';
import { Settings } from '@models/settings.model';
import { PixelFrame, celKey } from '@models/frame.model';
import { SpriteLayer, createBlankSpriteLayer } from '@models/layer.model';
import { FrameSnapshot } from '@store/reducers/frame.reducer';

/** Alias kept for backward compat in call sites. */
export type FrameHistorySnapshot = FrameSnapshot;

export interface ProjectEntry {
  id: string;
  name: string;
  updatedAt: number; // timestamp
  lastOpenedAt?: number; // timestamp
  thumbnail?: string; // base64 of first frame preview
}

export interface FramePersistenceData {
  frames: PixelFrame[];
  currentFrameId: string | null;
  layers?: SpriteLayer[];
  cels?: Record<string, string[][]>;
  activeLayerId?: string | null;
  past?: FrameSnapshot[];
  future?: FrameSnapshot[];
}

@Injectable({ providedIn: 'root' })
export class PersistenceService {
  private db: IDBPDatabase | null = null;
  private readonly DB_NAME = 'belaku-db';
  private readonly DB_VERSION = 2;

  constructor() { this.initDb(); }

  private async initDb(): Promise<void> {
    this.db = await openDB(this.DB_NAME, this.DB_VERSION, {
      upgrade(db, oldVersion) {
        if (!db.objectStoreNames.contains('db-store')) db.createObjectStore('db-store');
        if (!db.objectStoreNames.contains('projects'))  db.createObjectStore('projects');
        if (!db.objectStoreNames.contains('project-frames')) db.createObjectStore('project-frames');
      }
    });
  }

  private async ensureDb(): Promise<IDBPDatabase> {
    if (!this.db) await this.initDb();
    return this.db!;
  }

  // ── Project list ──────────────────────────────────────────────
  async listProjects(): Promise<ProjectEntry[]> {
    const db = await this.ensureDb();
    const all = await db.getAll('projects');
    return (all as ProjectEntry[]).sort((a,b) => {
      const aRecency = Math.max(a.updatedAt ?? 0, a.lastOpenedAt ?? 0);
      const bRecency = Math.max(b.updatedAt ?? 0, b.lastOpenedAt ?? 0);
      return bRecency - aRecency;
    });
  }

  async saveProject(
    id: string,
    name: string,
    frames: PixelFrame[],
    currentFrameId: string | null,
    layers?: SpriteLayer[],
    cels?: Record<string, string[][]>,
    activeLayerId?: string | null,
    past?: FrameHistorySnapshot[],
    future?: FrameHistorySnapshot[]
  ): Promise<void> {
    const db = await this.ensureDb();
    // Generate thumbnail from first frame
    let thumbnail: string | undefined;
    const f = frames[0];
    if (f) {
      const canvas = document.createElement('canvas');
      canvas.width = f.pixels[0]?.length ?? 1; canvas.height = f.pixels.length;
      const ctx = canvas.getContext('2d')!;
      const imgData = ctx.createImageData(canvas.width, canvas.height);
      for (let r = 0; r < f.pixels.length; r++) for (let c = 0; c < (f.pixels[0]?.length??0); c++) {
        const px = f.pixels[r][c] ?? '#00000000';
        const hexRaw = px === 'transparent' ? '00000000' : px.replace('#','');
        const hex = hexRaw.length === 6 || hexRaw.length === 8 ? hexRaw : '00000000';
        const i = (r*canvas.width+c)*4;
        imgData.data[i]   = parseInt(hex.slice(0,2),16);
        imgData.data[i+1] = parseInt(hex.slice(2,4),16);
        imgData.data[i+2] = parseInt(hex.slice(4,6),16);
        imgData.data[i+3] = hex.length === 8 ? parseInt(hex.slice(6,8),16) : 255;
      }
      ctx.putImageData(imgData,0,0);
      thumbnail = canvas.toDataURL('image/png');
    }
    const existing = await db.get('projects', id) as ProjectEntry | undefined;
    const entry: ProjectEntry = {
      id,
      name,
      updatedAt: Date.now(),
      lastOpenedAt: existing?.lastOpenedAt,
      thumbnail
    };
    await db.put('projects', entry, id);
    const existingData = await db.get('project-frames', id) as FramePersistenceData | undefined;
    await db.put('project-frames', {
      frames,
      layers:        layers        ?? existingData?.layers,
      cels:          cels          ?? existingData?.cels,
      activeLayerId: activeLayerId ?? existingData?.activeLayerId,
      currentFrameId,
      past:   past   ?? existingData?.past   ?? [],
      future: future ?? existingData?.future ?? [],
    } satisfies FramePersistenceData, id);
  }

  async touchProject(id: string): Promise<void> {
    const db = await this.ensureDb();
    const entry = await db.get('projects', id) as ProjectEntry | undefined;
    if (!entry) return;
    entry.lastOpenedAt = Date.now();
    await db.put('projects', entry, id);
  }

  async loadProject(id: string): Promise<FramePersistenceData | undefined> {
    const db = await this.ensureDb();
    return db.get('project-frames', id);
  }

  async deleteProject(id: string): Promise<void> {
    const db = await this.ensureDb();
    await db.delete('projects', id);
    await db.delete('project-frames', id);
  }

  // ── Legacy single-project compat (used by effects) ───────────
  async saveFrames(data: FramePersistenceData): Promise<void> {
    const db = await this.ensureDb();
    await db.put('db-store', data, 'frames');
  }

  async loadFrames(): Promise<FramePersistenceData | undefined> {
    const db = await this.ensureDb();
    return db.get('db-store', 'frames');
  }

  /**
   * Migrate persisted data from the old per-frame layers model to the new
   * global layers + sparse cels model.
   *
   * Old format: each PixelFrame had a `layers: LayerWithPixels[]` array and
   *   an `activeLayerId` field.
   * New format: `layers: SpriteLayer[]` is global, `cels` is a sparse map
   *   keyed by `frameId|layerId`, and PixelFrame only has `{ id, pixels }`.
   */
  migrateFrameData(raw: FramePersistenceData | undefined): {
    frames: PixelFrame[]; layers: SpriteLayer[]; cels: Record<string, string[][]>;
    activeLayerId: string | null; currentFrameId: string | null;
    past: FrameSnapshot[]; future: FrameSnapshot[];
  } {
    const empty = () => {
      const layer = createBlankSpriteLayer('Layer 1');
      return {
        frames: [] as PixelFrame[], layers: [layer], cels: {} as Record<string, string[][]>,
        activeLayerId: layer.id, currentFrameId: null as string | null,
        past: [] as FrameSnapshot[], future: [] as FrameSnapshot[],
      };
    };

    if (!raw) return empty();

    // Already migrated — has top-level layers array
    if (raw.layers && raw.layers.length > 0) {
      return {
        frames:        raw.frames       ?? [],
        layers:        raw.layers,
        cels:          raw.cels         ?? {},
        activeLayerId: raw.activeLayerId ?? raw.layers[0]?.id ?? null,
        currentFrameId: raw.currentFrameId ?? null,
        past:   ((raw.past  ?? []) as any[]).map((s: any) => this._migrateSnapshot(s)),
        future: ((raw.future ?? []) as any[]).map((s: any) => this._migrateSnapshot(s)),
      };
    }

    // Old format: derive canonical layer list from first frame's layers array
    const firstFrame = raw.frames[0] as any;
    const oldLayers: any[] = firstFrame?.layers ?? [];
    if (oldLayers.length === 0) return empty();

    // Strip per-frame `pixels` from layer objects to build global SpriteLayer list
    const layers: SpriteLayer[] = oldLayers.map(l => ({
      id:          l.id,
      name:        l.name,
      visible:     l.visible   ?? true,
      locked:      l.locked    ?? false,
      opacity:     l.opacity   ?? 100,
      blendMode:   l.blendMode ?? 'normal',
      tintColor:   l.tintColor ?? null,
      holdDuration: l.holdDuration ?? 1,
      isContinuous: l.isContinuous ?? false,
    }));

    // Build sparse cels map by matching layers across frames by position index
    const cels: Record<string, string[][]> = {};
    for (const frame of raw.frames as any[]) {
      const frameLayers: any[] = frame.layers ?? [];
      frameLayers.forEach((fl, i) => {
        const canonicalLayer = layers[i];
        if (!canonicalLayer) return;
        const pixels: string[][] = fl.pixels;
        if (!pixels) return;
        // Skip fully transparent cels to keep the sparse map lean
        const hasPixels = pixels.some(row => row.some(px => px && px !== '#00000000' && px !== 'transparent'));
        if (hasPixels) {
          cels[celKey(frame.id, canonicalLayer.id)] = pixels;
        }
      });
    }

    const activeLayerId = (firstFrame?.activeLayerId as string | undefined) ?? layers[0]?.id ?? null;

    // Strip old per-frame fields to produce clean PixelFrame objects
    const frames: PixelFrame[] = raw.frames.map(f => ({ id: (f as any).id, pixels: (f as any).pixels }));

    return {
      frames,
      layers,
      cels,
      activeLayerId,
      currentFrameId: raw.currentFrameId ?? null,
      past:   [],
      future: [],
    };
  }

  private _migrateSnapshot(s: any): FrameSnapshot {
    if (s.layers?.length) return s as FrameSnapshot; // already new format
    // Old snapshot: derive layers/cels from first frame if possible
    const firstFrame = (s.frames?.[0] as any);
    const oldLayers: any[] = firstFrame?.layers ?? [];
    const defaultLayer = createBlankSpriteLayer('Layer 1');
    if (!oldLayers.length) {
      return {
        frames: (s.frames ?? []).map((f: any) => ({ id: f.id, pixels: f.pixels })),
        currentFrameId: s.currentFrameId ?? null,
        layers: [defaultLayer],
        cels: {},
        activeLayerId: defaultLayer.id,
      };
    }
    const layers: SpriteLayer[] = oldLayers.map((l: any) => ({
      id: l.id, name: l.name, visible: l.visible ?? true, locked: l.locked ?? false,
      opacity: l.opacity ?? 100, blendMode: l.blendMode ?? 'normal',
      tintColor: l.tintColor ?? null, holdDuration: l.holdDuration ?? 1, isContinuous: l.isContinuous ?? false,
    }));
    const cels: Record<string, string[][]> = {};
    for (const frame of s.frames as any[]) {
      (frame.layers ?? []).forEach((fl: any, i: number) => {
        const cl = layers[i]; if (!cl || !fl.pixels) return;
        const hasPixels = fl.pixels.some((row: string[]) => row.some((px: string) => px && px !== '#00000000'));
        if (hasPixels) cels[celKey(frame.id, cl.id)] = fl.pixels;
      });
    }
    return {
      frames: (s.frames as any[]).map((f: any) => ({ id: f.id, pixels: f.pixels })),
      currentFrameId: s.currentFrameId,
      layers,
      cels,
      activeLayerId: (firstFrame?.activeLayerId as string | undefined) ?? layers[0]?.id ?? null,
    };
  }

  async saveSettings(settings: Settings): Promise<void> {
    const db = await this.ensureDb();
    await db.put('db-store', settings, 'settings');
  }

  async loadSettings(): Promise<Settings | undefined> {
    const db = await this.ensureDb();
    return db.get('db-store', 'settings');
  }

  async saveUserConfigState(state: UserConfig): Promise<void> {
    const db = await this.ensureDb();
    await db.put('db-store', state, 'userConfig');
  }

  async loadState(): Promise<UserConfig | undefined> {
    const db = await this.ensureDb();
    return db.get('db-store', 'userConfig');
  }
}
